import { Header, PageView, dataLayerConfig, useAppContext, useFacilityContext } from '@aspendental/shared-components-web';
import FooterLite from '@aspendental/shared-components-web/lib/components/FooterLite/FooterLite';
import RichText, { RichTextContent } from '@aspendental/shared-components-web/lib/components/RichText/RichText';
import { AdFooter } from '@aspendental/shared-components-web/lib/graphql/__generated/sdk';
import { Maybe } from '@aspendental/shared-components-web/lib/types';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ComponentProps } from 'react';
import { ReactNode, useEffect, useState } from 'react';
import PatientServicesHeader from '../../PatientServicesHeader/PatientServicesHeader';
import { needFurtherAssistanceContainer, patientServicesDesktopNavigationContainer } from './PortalLayout.styles';
import Footer from '@src/components/Footer/Footer';
import FurtherAssistanceContact from '@src/components/FurtherAssistanceContact';
import GlobalDialog from '@src/components/GlobalDialog';
import GlobalLoadingSpinner from '@src/components/GlobalLoadingSpinner';
import PatientServicesDesktopNavigation from '@src/components/PatientServicesNavigation/PatientServicesDesktopNavigation/PatientServicesDesktopNavigation';
import PatientServicesMobileNavigation from '@src/components/PatientServicesNavigation/PatientServicesMobileNavigation/PatientServicesMobileNavigation';
import SessionTimeoutWarning from '@src/components/SessionTimeoutWarning/SessionTimeoutWarning';
import { FORMS_BASE_URL, PATIENT_SERVICES_BASE_URL, SCHEDULER_BASE_URL, SESSION_KEY, SITE_META_DESCRIPTIONS, SITE_META_TITLE } from '@src/constants';
import { usePatientType } from '@src/hooks/patient-services/usePatientType';
import { usePortalSessionManager } from '@src/hooks/usePortalSessionManager';
import { App } from '@src/lib/__generated/contentful-sdk';
import { useGetFacility } from '@src/services/useGetFacility';
import { useAppStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import { getDeviceType } from '@src/utils/data-layer-utils';
import { displayNavigation } from '@src/utils/display-navigation';
import { isRouteProtected } from '@src/utils/middleware-patient-services-utils';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';
interface PortalLayoutProps {
  children: ReactNode;
  appData: Maybe<App>;
  footerData: Maybe<AdFooter>;
  isFooterLite?: boolean;
  withSimpleLayout?: boolean;
  locale: string;
  preview: boolean;
  logoHref: string;
  pageReferrer?: string;
  config?: BrandThemeConfig;
}
export const PortalLayout = ({
  appData,
  footerData,
  isFooterLite = true,
  withSimpleLayout = false,
  config,
  children,
  logoHref,
  pageReferrer
}: PortalLayoutProps) => {
  const theme = useTheme();
  const {
    pathname,
    push,
    query
  } = useRouter();
  const {
    appointmentId: visitId,
    returnTo
  } = query || {};
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    hasReasonForVisit
  } = useReasonForVisitStore(store => store);
  const {
    mottoFlow,
    languageToggleEnabled,
    setLanguageToggleEnabled,
    officeRollout: {
      loginLanguageToggle
    }
  } = useAppStore(store => store);
  const [isClient, setIsClient] = useState(false);
  const [patientServicesNavMenu, setPatientServicesNavMenu] = useState<boolean>(false);
  const geoLocation = usePatientInformationFormStore(store => store.geoLocation);
  const {
    latitude,
    longitude
  } = geoLocation;
  const sessionManager = usePortalSessionManager();
  const isOneClickLogin = !!visitId;
  const onScheduler = pathname.includes(SCHEDULER_BASE_URL);
  const onPatientServices = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}`);
  const onExpiration = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}/expiration`);
  const onAppointment = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}/appointment`);
  const onAuthentication = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}/authentication`);
  const onAccountInformation = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}/account-information`);
  const onDocumentsAndRecords = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}/documents-and-records`);
  const onLogin = pathname === `/${PATIENT_SERVICES_BASE_URL}` || pathname === `/${PATIENT_SERVICES_BASE_URL}/`;
  const facilityDetails = useFacilityContext();
  const {
    patientType,
    loading: patientTypeLoading
  } = usePatientType();
  const {
    data: facility
  } = useGetFacility({
    facilityCode: query?.fc ?? facilityDetails?.facilityCode
  });
  const enableNavigation = useAppStore(store => store.isPatientPortalNavigationEnabled);
  const {
    enablePortalRedesignNoAuth
  } = useAppStore(store => store.officeRollout);
  const {
    environment
  } = useAppContext();
  const handleLinkTracking = (linkText: string, linkUrl: string) => {
    dataLayerConfig({
      event: 'patient_services_dashboard',
      step_name: 'dashboard_action',
      link_text: linkText,
      link_url: environment === 'prod' ? 'https://www.aspendental.com' : 'https://wwwstg.aspendental.com' + linkUrl,
      auth_method: visitId ? 'one-click' : 'magic-link'
    });
    setPatientServicesNavMenu(false);
    push(linkUrl);
  };
  const magicLogout = async () => {
    setPatientServicesNavMenu(false);
    await sessionManager.logout();
  };
  const shouldDisplayFurtherAssistanceContact = isSmallScreen || !hasReasonForVisit && !mottoFlow || mottoFlow && latitude + longitude === 0;
  const showNavigation = displayNavigation(pathname, isOneClickLogin, returnTo);
  const mainBgColor = onPatientServices ? {
    xs: onAppointment || onDocumentsAndRecords || onAccountInformation ? 'backgrounds.neutral' : onLogin || onExpiration || onAuthentication ? 'backgrounds.default' : 'backgrounds.default',
    sm: onAppointment || onDocumentsAndRecords || onAccountInformation ? 'backgrounds.neutral' : onLogin || onExpiration || onAuthentication ? 'backgrounds.default' : 'backgrounds.neutral'
  } : {
    xs: 'backgrounds.default',
    sm: hasReasonForVisit && !mottoFlow || mottoFlow && latitude + longitude !== 0 ? 'backgrounds.neutral' : 'backgrounds.default'
  };
  const needFurtherAssistanceBgColor = onPatientServices ? {
    xs: onAppointment ? 'backgrounds.neutral' : onLogin || onExpiration || onAuthentication ? 'backgrounds.default' : 'backgrounds.default',
    sm: onAppointment ? 'backgrounds.neutral' : onLogin || onExpiration || onAuthentication ? 'backgrounds.default' : 'backgrounds.neutral'
  } : undefined;
  useEffect(() => {
    setIsClient(true);
    const scopedBaseline = document.getElementById('mui-scoped-baseline');
    if (scopedBaseline?.style) {
      scopedBaseline.style.height = '100vh';
    }
  }, []);

  // unset overflowX for sticky button on mobile
  useEffect(() => {
    const scopedBaseline = document.getElementById('mui-scoped-baseline');
    if (scopedBaseline?.style && isSmallScreen) {
      scopedBaseline.style.overflowX = 'unset';
    }
  }, [isSmallScreen]);
  useEffect(() => {
    const languageEnabled = onScheduler || loginLanguageToggle;
    if (languageEnabled) {
      setLanguageToggleEnabled(true);
    }
  }, [onScheduler, loginLanguageToggle, setLanguageToggleEnabled]);
  useEffect(() => {
    const sessionActive = sessionStorageHelper.getKey(SESSION_KEY);
    const isProtectedRoute = isRouteProtected(pathname);
    if (!sessionActive && isProtectedRoute && !isOneClickLogin && onPatientServices) {
      magicLogout();
    }
  }, [pathname, isOneClickLogin]);
  if (!isClient) {
    return null;
  }
  const facilityCode = query?.fc ?? facilityDetails?.facilityCode;
  const channel = query?.channel as string | undefined;
  const onForms = pathname.includes(`/${FORMS_BASE_URL}`);
  const analyticsContext = {
    event: 'virtual_page_view',
    channel: onForms ? channel : undefined,
    patient_type: onForms ? patientType : undefined,
    brand: mottoFlow ? 'mottoaligners' : 'aspendental',
    site_section: onPatientServices ? 'Patient Services' : 'Scheduling',
    virtual_page_url: globalThis?.location?.href ?? '',
    virtual_page_title: globalThis?.document?.title ?? '',
    device_type: getDeviceType(globalThis?.navigator?.userAgent ?? ''),
    website_selected_facility_id: facilityCode || '',
    ...(facility?.name && {
      website_selected_facility_name: facility.name
    }),
    ...(pageReferrer && {
      page_origin_url: pageReferrer
    })
  } as unknown as ComponentProps<typeof PageView>['analyticsContext'];
  const siteTitle = onPatientServices ? SITE_META_TITLE.portal : SITE_META_TITLE.schedule;
  const scheduleAppDescription = mottoFlow ? SITE_META_DESCRIPTIONS.motto : SITE_META_DESCRIPTIONS.standard;
  const siteDescription = onPatientServices ? SITE_META_DESCRIPTIONS.portal : scheduleAppDescription;

  // When motto config is selected and motto logos exist, substitute them as the standard logo properties
  // so the shared Header component can use them without knowing about motto-specific fields
  const isMottoConfig = config?.themeId === 'motto';
  const headerAppData = isMottoConfig && appData?.mottoLogo ? {
    ...appData,
    logo: appData.mottoLogo,
    logoMobile: appData.mottoLogoMobile || appData.mottoLogo
  } : appData;
  const banner = headerAppData?.header?.bottomAlertBanner;
  const mobileBannerJson = banner?.mobileAlertMessagesCollection?.items?.[0]?.content?.json;
  const desktopBannerJson = banner?.alertMessagesCollection?.items?.[0]?.content?.json;
  const bannerJson = (isSmallScreen && mobileBannerJson ? mobileBannerJson : desktopBannerJson) as RichTextContent | undefined;
  const shouldShowBanner = !!banner && (mottoFlow || isMottoConfig);

  // header height options do not include exact specs given for scheduling app, overwrite css to avoid polluting shared component
  const desktopHeaderOverwrites = {
    'header[data-test-id="section_header_desktop"]': {
      height: '5.5rem'
    },
    'header > div.MuiToolbar-root': {
      padding: '0 5%'
    }
  };
  return <>
			<Head data-sentry-element="Head" data-sentry-source-file="PortalLayout.tsx">
				<title>{siteTitle}</title>
				<meta name="description" content={siteDescription} data-react-helmet="true" data-sentry-element="meta" data-sentry-source-file="PortalLayout.tsx" />
				{environment !== 'prod' && <meta name="robots" content="noindex, nofollow, noarchive" />}
			</Head>
			{(!onForms || !patientTypeLoading) && <PageView siteSection={onPatientServices ? 'Patient Services' : 'Scheduling'} pageReferrer={pageReferrer} analyticsContext={analyticsContext} />}
			<Box id="scroll-ref" sx={{
      height: '0px'
    }} aria-hidden="true" data-sentry-element="Box" data-sentry-source-file="PortalLayout.tsx" />
			<Box display={'flex'} flexDirection={'column'} minHeight={'100vh'} data-sentry-element="Box" data-sentry-source-file="PortalLayout.tsx">
				{appData && <Box sx={{
        ...desktopHeaderOverwrites,
        ...(isMediumScreen && {
          minHeight: '5.5rem'
        })
      }}>
						{!onLogin && onPatientServices ? <PatientServicesHeader setPatientServicesNavMenu={setPatientServicesNavMenu} logout={magicLogout} /> : <>
								<Header appData={headerAppData!} withMenuCTA={false} hideLocation={true} hidePrimaryActionButton={true} logoHref={logoHref} showLanguageToggle={languageToggleEnabled} useHeaderLocationCards={false} hideHamburgerMenuToggleButton={onLogin || onScheduler} />
								{shouldShowBanner && <Box sx={{
            textAlign: 'center',
            padding: '0.5rem 0',
            backgroundColor: '#E5F2FF',
            height: isSmallScreen ? '0' : '3rem',
            alignContent: 'center',
            whiteSpace: 'pre-line'
          }}>
										{bannerJson && <RichText content={bannerJson} docProps={{
              fontWeight: '700',
              color: '#0B2341'
            }} />}
									</Box>}
							</>}
					</Box>}
				<Box sx={{
        bgcolor: enablePortalRedesignNoAuth ? 'backgrounds.neutral' : mainBgColor
      }} data-sentry-element="Box" data-sentry-source-file="PortalLayout.tsx">
					{enableNavigation && showNavigation && isMediumScreen ? <PatientServicesDesktopNavigation magicLogout={magicLogout} handleLinkTracking={handleLinkTracking}>
							<Box sx={patientServicesDesktopNavigationContainer({
            isMediumScreen
          })}>{children}</Box>
						</PatientServicesDesktopNavigation> : <>
							{enableNavigation && showNavigation && !isMediumScreen && <PatientServicesMobileNavigation magicLogout={magicLogout} handleLinkTracking={handleLinkTracking} isHamburgerMenuOpen={patientServicesNavMenu} closeHamburgerMenu={setPatientServicesNavMenu} />}
							{children}
							{shouldDisplayFurtherAssistanceContact && !onPatientServices && <Grid sx={needFurtherAssistanceContainer({
            needFurtherAssistanceBgColor
          })} id="need-further-assistance">
									<FurtherAssistanceContact />
								</Grid>}
						</>}
				</Box>
				{footerData && isFooterLite && <FooterLite footerData={footerData} />}
				{footerData && !isFooterLite && <Footer footerData={footerData} withScheduling={withSimpleLayout} showDisclaimer={shouldShowBanner} />}
				<GlobalDialog data-sentry-element="GlobalDialog" data-sentry-source-file="PortalLayout.tsx" />
				<GlobalLoadingSpinner data-sentry-element="GlobalLoadingSpinner" data-sentry-source-file="PortalLayout.tsx" />
				{sessionManager.sessionWarning.show && <SessionTimeoutWarning sessionManager={sessionManager} />}
				<Box sx={{
        flexGrow: 1
      }} bgcolor="primary.main" data-sentry-element="Box" data-sentry-source-file="PortalLayout.tsx" />
			</Box>
		</>;
};